import React, { useState } from 'react';
import { AlertTriangle, Check, Dices, Loader2, Undo2 } from 'lucide-react';
import {
  ACCESSORIES,
  BACKGROUNDS,
  CLOTHING,
  EYEBROWS,
  EYES,
  FACE_DETAILS,
  FACIAL_HAIR,
  GLASSES,
  HAIR_COLORS,
  HAIR_STYLES,
  MOUTHS,
  OUTFIT_COLORS,
  SKIN_COLORS,
  LASHES,
  LIPSTICK,
  EARRINGS,
  EYE_COLORS,
  normalizeAvatar,
  type AvatarConfig,
} from '../../shared/avatar';
import { Avatar, randomAvatar } from './Avatar';

interface AvatarCustomizerProps {
  initialConfig: unknown;
  /** Si devuelve una promesa, el taller enseña el guardado y el error en el propio botón. */
  onSave: (config: AvatarConfig) => void | Promise<void>;
  onClose?: () => void;
  /**
   * Dentro de otro panel (el modal de perfil) no lleva tarjeta propia: una
   * tarjeta dentro de otra solo añade bordes.
   */
  embedded?: boolean;
}

type Tab = 'cara' | 'pelo' | 'ojos' | 'boca' | 'ropa' | 'extras' | 'fondo';

// Nombres cortos a propósito: las siete pestañas tienen que caber a la vista.
// Una pestaña escondida tras un desplazamiento horizontal es una pestaña que
// nadie encuentra.
const TABS: { id: Tab; label: string }[] = [
  { id: 'cara', label: 'Cara' },
  { id: 'pelo', label: 'Pelo' },
  { id: 'ojos', label: 'Ojos' },
  { id: 'boca', label: 'Boca' },
  { id: 'ropa', label: 'Ropa' },
  { id: 'extras', label: 'Extras' },
  { id: 'fondo', label: 'Fondo' },
];

type EnumKey = {
  [K in keyof AvatarConfig]: AvatarConfig[K] extends string ? (string extends AvatarConfig[K] ? never : K) : never;
}[keyof AvatarConfig];
type ColorKey = 'skinColor' | 'hairColor' | 'eyeColor' | 'clothingColor' | 'accessoryColor';

type SaveState = { tipo: 'quieto' } | { tipo: 'guardando' } | { tipo: 'guardado' } | { tipo: 'error'; mensaje: string };

const sameAvatar = (a: AvatarConfig, b: AvatarConfig) =>
  (Object.keys(a) as (keyof AvatarConfig)[]).every((k) => a[k] === b[k]);

/**
 * Taller de personaje.
 *
 * Cada opción se enseña con una miniatura del propio personaje llevándola, no
 * con una palabra: «Decidido» o «Perilla» no dicen nada hasta que se ven. Las
 * piezas de la cara usan un encuadre cerrado porque a 56 px, con el cuerpo
 * entero, unos ojos u otros son indistinguibles.
 *
 * Con sitio (el modal de perfil ancho) se reparte en dos columnas: el
 * personaje grande y quieto a la izquierda y las opciones a la derecha, para
 * no perderlo de vista mientras se prueban cosas. Pasar el ratón por una
 * opción la enseña en grande sin elegirla todavía.
 */
export const AvatarCustomizer: React.FC<AvatarCustomizerProps> = ({
  initialConfig,
  onSave,
  onClose,
  embedded = false,
}) => {
  const [saved, setSaved] = useState<AvatarConfig>(() => normalizeAvatar(initialConfig));
  const [config, setConfig] = useState<AvatarConfig>(saved);
  const [history, setHistory] = useState<AvatarConfig[]>([]);
  const [tab, setTab] = useState<Tab>('cara');
  const [hover, setHover] = useState<Partial<AvatarConfig> | null>(null);
  const [saveState, setSaveState] = useState<SaveState>({ tipo: 'quieto' });

  const dirty = !sameAvatar(config, saved);
  const preview = hover ? { ...config, ...hover } : config;

  const update = (patch: Partial<AvatarConfig>) => {
    setHistory((h) => [...h.slice(-29), config]);
    setConfig((c) => ({ ...c, ...patch }));
    setHover(null);
    setSaveState({ tipo: 'quieto' });
  };

  const undo = () => {
    const prev = history[history.length - 1];
    if (!prev) return;
    setHistory((h) => h.slice(0, -1));
    setConfig(prev);
    setHover(null);
    setSaveState({ tipo: 'quieto' });
  };

  const save = async () => {
    const result = onSave(config);
    if (!(result instanceof Promise)) return;
    setSaveState({ tipo: 'guardando' });
    try {
      await result;
      setSaved(config);
      setSaveState({ tipo: 'guardado' });
    } catch (err) {
      setSaveState({ tipo: 'error', mensaje: err instanceof Error ? err.message : 'No se pudo guardar.' });
    }
  };

  const hoverProps = (patch: Partial<AvatarConfig>) => ({
    onMouseEnter: () => setHover(patch),
    onMouseLeave: () => setHover(null),
    onFocus: () => setHover(patch),
    onBlur: () => setHover(null),
  });

  const options = <K extends EnumKey>(
    title: string,
    key: K,
    list: readonly { id: string; label: string }[],
    crop: 'full' | 'face' = 'full',
  ) => (
    <Section title={title}>
      <div className="grid grid-cols-3 min-[400px]:grid-cols-4 @xl:grid-cols-5 gap-2">
        {list.map((o) => {
          const selected = config[key] === o.id;
          const patch = { [key]: o.id } as Partial<AvatarConfig>;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => update(patch)}
              {...hoverProps(patch)}
              aria-pressed={selected}
              title={o.label}
              className={`flex flex-col items-center gap-1 p-1.5 rounded-xl border transition-colors cursor-pointer ${
                selected
                  ? 'border-sky-400 bg-sky-500/15'
                  : 'border-ink-700 bg-ink-850 hover:border-ink-600 hover:bg-ink-800'
              }`}
            >
              <Avatar config={{ ...config, ...patch }} size="fill" crop={crop} />
              <span className={`text-[0.6875rem] leading-tight truncate max-w-full ${selected ? 'text-ink-50 font-semibold' : 'text-ink-400'}`}>
                {o.label}
              </span>
            </button>
          );
        })}
      </div>
    </Section>
  );

  const swatches = (title: string, key: ColorKey, colors: readonly string[]) => (
    <Section title={title}>
      <div className="flex flex-wrap gap-2">
        {colors.map((color) => {
          const selected = config[key].toUpperCase() === color.toUpperCase();
          const patch = { [key]: color } as Partial<AvatarConfig>;
          return (
            <button
              key={color}
              type="button"
              onClick={() => update(patch)}
              {...hoverProps(patch)}
              aria-pressed={selected}
              aria-label={`Color ${color}`}
              className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-transform cursor-pointer ${
                selected ? 'border-sky-400 scale-110' : 'border-ink-700 hover:scale-110'
              }`}
              style={{ backgroundColor: color }}
            >
              {selected && <Check className="w-4 h-4 stroke-[3] text-white mix-blend-difference" />}
            </button>
          );
        })}
      </div>
    </Section>
  );

  const saveLabel =
    saveState.tipo === 'guardando' ? 'Guardando…' : saveState.tipo === 'guardado' && !dirty ? 'Guardado' : 'Guardar';

  const body = (
    <div className="@container">
      <div className="grid gap-4 @xl:grid-cols-[13rem_1fr] @xl:gap-6">
        {/* Personaje: arriba en estrecho, columna fija en ancho */}
        <div className="flex items-center gap-4 @xl:flex-col @xl:items-stretch @xl:self-start @xl:sticky @xl:top-0">
          <Avatar
            config={preview}
            size="fill"
            className="w-24! min-[400px]:w-28! @xl:w-full! shadow-lg transition-none"
          />
          <div className="flex-1 min-w-0 @xl:flex-none">
            <h2 className="font-display font-semibold text-[1.0625rem] text-ink-50 @xl:hidden">Tu personaje</h2>
            <p className="text-ink-400 text-[0.8125rem] leading-snug mt-0.5 @xl:mt-0 @xl:text-center">
              Así te verán en la sala, en las puntuaciones y en el podio.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={() => update(randomAvatar())}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 h-9 rounded-lg text-[0.8125rem] font-semibold border border-ink-700 bg-ink-850 text-ink-100 hover:bg-ink-800 transition-colors cursor-pointer"
              >
                <Dices className="w-4 h-4" />
                Aleatorio
              </button>
              <button
                type="button"
                onClick={undo}
                disabled={history.length === 0}
                aria-label="Deshacer"
                title="Deshacer"
                className="flex items-center justify-center w-9 h-9 rounded-lg border border-ink-700 bg-ink-850 text-ink-100 hover:bg-ink-800 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-default"
              >
                <Undo2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Opciones */}
        <div className="flex flex-col gap-3 min-w-0">
          <div role="tablist" className="grid grid-cols-4 min-[420px]:grid-cols-7 gap-1 p-1 rounded-lg bg-ink-850 border border-ink-700">
            {TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`px-1 py-1.5 rounded-md text-[0.8125rem] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  tab === t.id ? 'bg-ink-700 text-ink-50' : 'text-ink-400 hover:text-ink-100'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div
            className="flex flex-col gap-4 max-h-[44vh] @xl:h-[26rem] @xl:max-h-[56vh] overflow-y-auto pr-1 -mr-1"
            onMouseLeave={() => setHover(null)}
          >
            {tab === 'cara' && (
              <>
                {swatches('Tono de piel', 'skinColor', SKIN_COLORS)}
                {options('Detalles', 'detail', FACE_DETAILS, 'face')}
              </>
            )}
            {tab === 'pelo' && (
              <>
                {options('Peinado', 'hairStyle', HAIR_STYLES)}
                {swatches('Color de pelo', 'hairColor', HAIR_COLORS)}
                {options('Vello facial', 'facialHair', FACIAL_HAIR, 'face')}
              </>
            )}
            {tab === 'ojos' && (
              <>
                {options('Mirada', 'eyes', EYES, 'face')}
                {swatches('Color de ojos', 'eyeColor', EYE_COLORS)}
                {options('Pestañas', 'lashes', LASHES, 'face')}
                {options('Cejas', 'eyebrows', EYEBROWS, 'face')}
                {options('Gafas', 'glasses', GLASSES, 'face')}
              </>
            )}
            {tab === 'boca' && (
              <>
                {options('Expresión', 'mouth', MOUTHS, 'face')}
                {options('Labios', 'lipstick', LIPSTICK, 'face')}
              </>
            )}
            {tab === 'ropa' && (
              <>
                {options('Prenda', 'clothing', CLOTHING)}
                {swatches('Color de la ropa', 'clothingColor', OUTFIT_COLORS)}
              </>
            )}
            {tab === 'extras' && (
              <>
                {options('Accesorio', 'accessory', ACCESSORIES)}
                {options('Pendientes', 'earrings', EARRINGS, 'face')}
                {swatches('Color del accesorio, la pintura y los colgantes', 'accessoryColor', OUTFIT_COLORS)}
              </>
            )}
            {tab === 'fondo' && options('Fondo', 'background', BACKGROUNDS)}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-ink-700">
        <p className="flex-1 min-w-0 text-[0.8125rem] leading-snug">
          {saveState.tipo === 'error' ? (
            <span className="flex items-center gap-1.5 text-danger-400">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {saveState.mensaje}
            </span>
          ) : saveState.tipo === 'guardado' && !dirty ? (
            <span className="flex items-center gap-1.5 text-success-400">
              <Check className="w-4 h-4 shrink-0" />
              Personaje guardado
            </span>
          ) : dirty ? (
            <span className="text-ember-300">Cambios sin guardar</span>
          ) : null}
        </p>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 whitespace-nowrap px-3 h-11 rounded-lg text-sm font-medium text-ink-400 hover:text-ink-100 transition-colors cursor-pointer"
          >
            Cancelar
          </button>
        )}
        <button
          type="button"
          onClick={() => void save()}
          disabled={!dirty || saveState.tipo === 'guardando'}
          className="shrink-0 flex items-center justify-center gap-2 px-5 h-11 rounded-lg font-semibold text-sm whitespace-nowrap bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default disabled:hover:bg-ember-500"
        >
          {saveState.tipo === 'guardando' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
          {saveLabel}
        </button>
      </div>
    </div>
  );

  if (embedded) return body;
  return <div className="surface-raised bg-ink-900 p-5 w-full">{body}</div>;
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div>
    <p className="text-ink-400 text-xs uppercase tracking-wider font-semibold mb-2">{title}</p>
    {children}
  </div>
);
